// assets/search-drawer.js
(function () {
  // Simple debounce
  function debounce(fn, wait = 300) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  class DrawerSearchSection extends HTMLElement {
    constructor() {
      super();

      // State
      this.isOpen = false;
      this.drawerClass = "wt-drawer-search";
      // IMPORTANT: scope the drawer to this custom element instance
      this.drawer = this; // <search-drawer> is the drawer wrapper
      this.classDrawerActive = `${this.drawerClass}--active`;
      this.pageOverlayClass = "search-overlay";
      this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
      this.body = document.body;

      // Query selectors to find triggers *inside this drawer only*
      this.triggerQuery = [
        ".wt-header__search-trigger",
        ".wt-header__search__close",
      ].join(", ");
      this.triggers = () => this.querySelectorAll(this.triggerQuery);

      // Search elements (scoped)
      this.cachedResults = {};
      this.input = this.querySelector('input[name="q"]');
      this.clearButton = this.querySelector(".wt-header__search__clear-button");
      this.closeButton = this.querySelector(".wt-header__search__close");
      this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
      this.mainTrigger = this.querySelector(".wt-header__search-trigger");
      this.emptyAnnouncement = this.querySelector(".search-empty");

      this.isVisibleClearButton = false;

      // Bind context for design mode handler
      this.saveSuggestionMenuInDesignMode = this.saveSuggestionMenuInDesignMode.bind(this);

      // Setup
      this.setupEventListeners();
      this.toggleTabindexElements = [this.input, this.closeButton];
      this.init();
    }

    // Returns first/last focusable elements within this drawer
    getFocusableElements() {
      const focusableElementsSelector =
        "button, [href], input:not([type='hidden']), select, [tabindex]";
      const focusableElements = () =>
        Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
          (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
        );

      const list = focusableElements();
      return {
        focusableElements: list,
        first: list[0],
        last: list[list.length - 1],
      };
    }

    onToggle() {
      if (this.hasAttribute("open")) {
        this.removeAttribute("open");
        setTabindex(this.toggleTabindexElements, "-1");
        setTabindex([this.mainTrigger], "0");
        this.isOpen = false;
        setTimeout(() => {
          if (this.mainTrigger && typeof this.mainTrigger.focus === "function") {
            this.mainTrigger.focus();
          }
        }, 0);
      } else {
        this.setAttribute("open", "");
        setTabindex(this.toggleTabindexElements, "0");
        setTabindex([this.mainTrigger], "-1");
        if (this.input && typeof this.input.focus === "function") this.input.focus();
        this.isOpen = true;
      }
    }

    toggleDrawerClasses() {
      this.onToggle();
      // toggle classes on the scoped drawer and body
      if (this.drawer) this.drawer.classList.toggle(this.classDrawerActive);
      if (this.body) this.body.classList.toggle(this.activeOverlayBodyClass);
    }

    init() {
      // Ensure body is properly reset on initialization
      this.resetBodyStyles();
      
      // Attach click handlers only to triggers inside this drawer
      const triggers = this.triggers();
      if (triggers && triggers.length) {
        triggers.forEach((trigger) => {
          // Avoid adding duplicate handlers if called more than once
          trigger.addEventListener("click", (e) => {
            e.preventDefault();
            this.toggleDrawerClasses();
          });
        });
      }

      if (this.clearButton) {
        this.clearButton.addEventListener("click", () => {
          if (this.input) this.input.value = "";
          this.clearButton.style.display = "none";
          this.isVisibleClearButton = false;
          this.clearResults();
        });
      }

      // Key trap and escape handling inside this drawer
      this.addEventListener("keydown", (e) => {
        const isTabPressed = e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
        const { first, last } = this.getFocusableElements();

        if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
          if (this.isOpen) {
            this.toggleDrawerClasses();
          }
        }

        if (isTabPressed && this.isOpen) {
          if (e.shiftKey && document.activeElement === first) {
            last.focus();
            e.preventDefault();
          } else if (!e.shiftKey && document.activeElement === last) {
            first.focus();
            e.preventDefault();
          }
        }
      });

      // Show/hide clear button based on input content
      if (this.input) {
        this.input.addEventListener("input", () => {
          if (this.input.value.length > 0 && !this.isVisibleClearButton) {
            if (this.clearButton) {
              this.clearButton.style.display = "flex";
              this.clearButton.setAttribute("tabindex", "0");
            }
            this.isVisibleClearButton = true;
          } else if (this.input.value.length === 0 && this.isVisibleClearButton) {
            if (this.clearButton) {
              this.clearButton.style.display = "none";
              this.clearButton.setAttribute("tabindex", "-1");
            }
            this.isVisibleClearButton = false;
          }
        });
      }
    }

    // Set up form and predictive search listeners (scoped)
    setupEventListeners() {
      const form = this.querySelector("form.store-search-form");
      if (form) {
        form.addEventListener("submit", this.onFormSubmit.bind(this));
      }

      if (this.input) {
        // debounce the input handler
        this.input.addEventListener(
          "input",
          debounce((event) => {
            this.onChange(event);
          }, 300).bind(this),
        );
      }

      if (Shopify && Shopify.designMode) {
        document.addEventListener("shopify:section:load", this.saveSuggestionMenuInDesignMode);
      }
    }

    saveSuggestionMenuInDesignMode() {
      // when in design mode, toggle active class if overlay present (keeps preview consistent)
      if (this.body.classList.contains(this.activeOverlayBodyClass) && this.drawer) {
        this.drawer.classList.toggle(this.classDrawerActive);
      }
    }

    getQuery() {
      return this.input ? this.input.value.trim() : "";
    }

    onChange() {
      const searchTerm = this.getQuery();

      if (searchTerm.length) {
        this.getSearchResults(searchTerm);
        this.removeAttribute("empty");
      } else {
        this.clearResults();
      }
    }

    onFormSubmit(event) {
      // Prevent submit if no query or if an aria-selected predictive result exists
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
        event.preventDefault();
      }
    }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      // Use Shopify's predictive_search_url route (routes.predictive_search_url comes from theme)
      fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent(
          "resources[type]",
        )}=product,page,article,collection,query&${encodeURIComponent("resources[limit_scope]")}=each&${encodeURIComponent(
          "resources[limit]",
        )}=6&${encodeURIComponent("resources[options][fields]")}=title,variants.sku,product_type&section_id=predictive-search`,
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.status);
          }
          return response.text();
        })
        .then((text) => {
          // Parse section HTML and extract the predictive search markup
          const resultsMarkup = new DOMParser()
            .parseFromString(text, "text/html")
            .querySelector("#shopify-section-predictive-search")?.innerHTML;

          if (resultsMarkup) {
            this.cachedResults[queryKey] = resultsMarkup;
            this.renderSearchResults(resultsMarkup);
          }
        })
        .catch((error) => {
          // fail silently (or log)
          // console.error('Predictive search fetch error', error);
        });
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
      this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

      if (this.statusElement) {
        this.setLiveRegionText(this.loadingText);
      }
      this.setAttribute("loading", true);
    }

    setLiveRegionText(statusText) {
      if (!this.statusElement) return;
      this.statusElement.setAttribute("aria-hidden", "false");
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute("aria-hidden", "true");
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      if (!this.predictiveSearchResults) return;
      this.predictiveSearchResults.innerHTML = resultsMarkup;

      this.setAttribute("results", true);

      this.setLiveRegionResults();
    }

    setLiveRegionResults() {
      this.removeAttribute("loading");
      const countEl = this.querySelector("[data-predictive-search-live-region-count-value]");
      this.setLiveRegionText(countEl ? countEl.textContent : "");
    }

    clearResults() {
      if (this.input) this.input.value = "";
      this.removeAttribute("results");
      this.setAttribute("empty", true);
      if (this.predictiveSearchResults) this.predictiveSearchResults.innerHTML = "";
    }

    resetBodyStyles() {
      // Reset any stuck body styles that might prevent scrolling
      if (this.body) {
        this.body.classList.remove(this.activeOverlayBodyClass);
      }
    }
  }

  // Helper used in the class - set tabindex on list of elements
  function setTabindex(elements, value) {
    if (!elements || !elements.length) return;
    elements.forEach((el) => {
      if (!el) return;
      try {
        if (value === null) el.removeAttribute("tabindex");
        else el.setAttribute("tabindex", value);
      } catch (e) {
        // ignore invalid element
      }
    });
  }

  // Define the custom element if not already defined
  if (!customElements.get("search-drawer")) {
    customElements.define("search-drawer", DrawerSearchSection);
  }
})();
